import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { BarChart3, Activity, RefreshCw, TrendingUp } from 'lucide-react';

interface RunningTrainingLoadChartProps {
  timeRange: string;
}

const chartConfig = {
  load: {
    label: "Training Load",
    color: "hsl(var(--chart-1))",
  },
};

export const RunningTrainingLoadChart: React.FC<RunningTrainingLoadChartProps> = ({ timeRange }) => {
  const { user } = useAuth();

  const { data: loadData, isLoading, refetch } = useQuery({
    queryKey: ['running-training-load', user?.id, timeRange],
    queryFn: async () => {
      if (!user) return null;

      const { data: recentRuns } = await supabase
        .from('running_performance')
        .select('*')
        .eq('user_id', user.id)
        .order('recorded_date', { ascending: false })
        .limit(28); // 4 weeks of data

      if (!recentRuns || recentRuns.length === 0) {
        return null;
      }

      // Group runs by week
      const weeklyData = [];
      for (let week = 0; week < 4; week++) {
        const weekStart = new Date();
        weekStart.setDate(weekStart.getDate() - (week + 1) * 7);
        const weekEnd = new Date();
        weekEnd.setDate(weekEnd.getDate() - week * 7);

        const weekRuns = recentRuns.filter(run => {
          const runDate = new Date(run.recorded_date);
          return runDate >= weekStart && runDate < weekEnd;
        });

        const weeklyDistance = weekRuns.reduce((sum, run) => sum + (run.distance_meters || 0), 0) / 1000;
        const weeklyDuration = weekRuns.reduce((sum, run) => sum + (run.time_seconds || 0), 0) / 3600; // hours
        const avgSufferScore = weekRuns.filter(r => r.suffer_score).length > 0 ?
          weekRuns.filter(r => r.suffer_score).reduce((sum, r) => sum + (r.suffer_score || 0), 0) / weekRuns.filter(r => r.suffer_score).length : 0;

        // Simple training load calculation: distance * intensity factor
        const trainingLoad = weeklyDistance * (avgSufferScore > 0 ? avgSufferScore / 50 : 1.2);

        weeklyData.push({
          week: `Week ${4 - week}`,
          weekIndex: 4 - week,
          distance: weeklyDistance,
          duration: weeklyDuration,
          runCount: weekRuns.length,
          load: Math.round(trainingLoad),
          sufferScore: avgSufferScore,
        });
      }

      const totalLoad = weeklyData.reduce((sum, week) => sum + week.load, 0);
      const avgLoad = totalLoad / 4;
      const currentWeekLoad = weeklyData[3]?.load || 0;
      const previousWeekLoad = weeklyData[2]?.load || 0;
      const loadTrend = previousWeekLoad > 0 ? ((currentWeekLoad - previousWeekLoad) / previousWeekLoad) * 100 : 0;

      return {
        weeklyData: weeklyData.reverse(), // Show chronologically
        totalLoad,
        avgLoad,
        currentWeekLoad,
        loadTrend,
      };
    },
    enabled: !!user,
    staleTime: 15 * 60 * 1000,
  });

  const getLoadStatus = (load: number) => {
    if (load < 20) return { status: 'Low', color: 'text-green-600', bg: 'bg-green-100' };
    if (load < 40) return { status: 'Moderate', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    if (load < 60) return { status: 'High', color: 'text-orange-600', bg: 'bg-orange-100' };
    return { status: 'Very High', color: 'text-red-600', bg: 'bg-red-100' };
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-purple-600" />
            Training Load Chart
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-40 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!loadData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-purple-600" />
            Training Load Chart
          </CardTitle>
          <CardDescription>Training stress and load over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Activity className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p className="text-gray-500 mb-4">Need more training data for load analysis</p>
            <Button onClick={() => refetch()} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentLoadStatus = getLoadStatus(loadData.currentWeekLoad);

  return (
    <Card className="border-l-4 border-l-purple-500">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-purple-600" />
            <CardTitle>Training Load Chart</CardTitle>
            <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs">
              4-Week View
            </Badge>
          </div>
          <Button onClick={() => refetch()} variant="ghost" size="sm">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <CardDescription>Training stress and load analysis over time</CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Current Load Status */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`${currentLoadStatus.bg} p-4 rounded-lg`}>
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className={`h-4 w-4 ${currentLoadStatus.color}`} />
              <span className="text-sm font-medium">Current Load</span>
            </div>
            <div className={`text-2xl font-bold ${currentLoadStatus.color}`}>
              {loadData.currentWeekLoad}
            </div>
            <p className={`text-sm ${currentLoadStatus.color}`}>{currentLoadStatus.status}</p>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium">4-Week Average</span>
            </div>
            <div className="text-2xl font-bold text-blue-700">
              {Math.round(loadData.avgLoad)}
            </div>
            <p className="text-sm text-blue-600">Training Load</p>
          </div>

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="h-4 w-4 text-indigo-600" />
              <span className="text-sm font-medium">Load Trend</span>
            </div>
            <div className="text-2xl font-bold text-indigo-700">
              {loadData.loadTrend !== 0 ? (
                <>
                  {loadData.loadTrend > 0 ? '+' : ''}{loadData.loadTrend.toFixed(1)}%
                </>
              ) : '0%'}
            </div>
            <p className="text-sm text-indigo-600">vs. Last Week</p>
          </div>
        </div>

        {/* Training Load Chart */}
        <div>
          <h4 className="font-medium text-sm mb-4">Weekly Training Load Progression</h4>
          <ChartContainer config={chartConfig} className="h-64 w-full">
            <BarChart data={loadData.weeklyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar 
                dataKey="load" 
                fill="var(--color-load)" 
                radius={[4, 4, 0, 0]}
                name="Training Load"
              />
            </BarChart>
          </ChartContainer>
        </div>

        {/* Weekly Breakdown */}
        <div>
          <h4 className="font-medium text-sm mb-3">Weekly Load Breakdown</h4>
          <div className="space-y-2">
            {loadData.weeklyData.map((week, index) => {
              const weekStatus = getLoadStatus(week.load);
              const isCurrentWeek = index === loadData.weeklyData.length - 1;
              return (
                <div 
                  key={week.week} 
                  className={`flex items-center justify-between p-3 rounded-lg ${isCurrentWeek ? 'ring-2 ring-purple-200 bg-purple-50' : 'bg-gray-50'}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-sm">
                      {week.week} {isCurrentWeek && '(Current)'}
                    </span>
                    <Badge className={`${weekStatus.bg} ${weekStatus.color}`} variant="secondary">
                      Load: {week.load}
                    </Badge>
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    <div>{week.distance.toFixed(1)}km • {week.runCount} runs</div>
                    {week.sufferScore > 0 && (
                      <div className="text-xs">Avg Suffer: {Math.round(week.sufferScore)}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};